import { useParams, useNavigate } from 'react-router-dom'
import { Stack, Alert } from '@mui/material'
import Grid from '@mui/material/Grid2'
import { SPACING } from '@common/theme/spacing'
import { SIZING } from '@common/theme/sizing'
import { useGetTimePointSubmissionDetails } from '@features/subject/hooks/useGetTimePointSubmissionDetails'
import {
  useGetSubjectById,
  useGetSubjectTimepoints,
} from '@features/study_details/Subjects/hooks/useStudySubjectsQueries'
import { useGetStudySubjectConfiguration } from '@study_setup/SubjectConfiguration/hooks/useStudySubjectConfigurationQueries'
import { SubjectsTimepointSchema } from '@common/config/api/client/types.gen'
import { useForm, FormProvider } from 'react-hook-form'
import { SubjectDetailsFormValues } from '@features/subject/subject.types'
import { useLoadStudySubjectDetails } from '@features/study_details/Subjects/hooks/useLoadStudySubjectDetails'
import SubjectTimePointConfiguration from '../SubjectTimePointConfiguration/index'
import TimePointManager from '../TimePointManager/index'
import { findSubmissionById } from '../../utils'
import {
  getViewableSubmissions,
  useOpenAllTimepointsInViewer,
} from '@features/subject/hooks/useOpenAllTimepointsInViewer'
import { useEffect } from 'react'
import { generatePathWithParams } from '@common/utils/urlUtils'
import { ROUTES } from 'src/routes'
import { useTheme } from '@mui/material/styles'
import { useSearchParams, createSearchParams } from 'react-router-dom'

export default function SubjectTimepointSubmission() {
  const navigate = useNavigate()
  const theme = useTheme()

  const { studyId, subjectId, timepointSubmissionId } = useParams<{
    studyId: string
    subjectId: string
    timepointSubmissionId?: string
  }>()

  const {
    data: subjectData,
    isError,
    isLoading: isSubjectLoading,
  } = useGetSubjectById({
    study_id: studyId!,
    subject_id: subjectId!,
  })

  const [searchParams] = useSearchParams()
  const limit = searchParams.get('limit')
  const prev_cursor = searchParams.get('previous_cursor')
  const next_cursor = searchParams.get('next_cursor')

  const { data: studySubjectConfigData, isLoading: isSubjectConfigLoading } = useGetStudySubjectConfiguration(studyId!)

  const { data: subjectTimepointsData, isLoading: isSubjectTimepointsLoading } = useGetSubjectTimepoints({
    study_id: studyId!,
    subject_id: subjectId!,
  })

  const { data: timePointSubmissionData, isLoading: isTimePointSubmissionLoading } = useGetTimePointSubmissionDetails(
    timepointSubmissionId ? ['TimePointsDetails', timepointSubmissionId] : [],
    {
      study_id: studyId!,
      subject_id: subjectId!,
      timepoint_submission_id: timepointSubmissionId!,
    },
  )

  const methods = useForm<SubjectDetailsFormValues>()
  useLoadStudySubjectDetails(methods, studyId!, subjectData, studySubjectConfigData)
  const viewableSubmissions = getViewableSubmissions(subjectTimepointsData, subjectId)
  const { handleOpenAllInViewer, modalityOptions } = useOpenAllTimepointsInViewer(
    studyId,
    subjectId,
    viewableSubmissions,
  )

  useEffect(() => {
    if (!timepointSubmissionId && !isSubjectTimepointsLoading && subjectTimepointsData) {
      const subject = subjectTimepointsData.subjects?.find((s) => s.subject_id === subjectId)

      if (subject?.study_timepoints && subject.study_timepoints.length > 0) {
        for (const timepoint of subject.study_timepoints) {
          if (timepoint.timepoint_submissions && timepoint.timepoint_submissions.length > 0) {
            const firstSubmission = timepoint.timepoint_submissions[0]
            const path = generatePathWithParams(ROUTES.SUBJECT_TIMEPOINT_SUBMISSION.path, {
              studyId,
              subjectId,
              timepointSubmissionId: firstSubmission.timepoint_submission_id,
            })
            const searchParams = {
              limit: limit!,
              previous_cursor: prev_cursor!,
              next_cursor: next_cursor!,
            }
            navigate({ pathname: path, search: `?${createSearchParams(searchParams!)}` })
            return
          }
        }
      }
    }
  }, [
    timepointSubmissionId,
    isSubjectTimepointsLoading,
    subjectTimepointsData,
    studyId,
    subjectId,
    navigate,
    limit,
    prev_cursor,
    next_cursor,
  ])

  if (isSubjectLoading || isSubjectConfigLoading || isSubjectTimepointsLoading) {
    return <div>Loading...</div>
  }

  if (isError) {
    return <div data-testid="error-message">Failed to load subject</div>
  }

  const transformTimePoints = (data: SubjectsTimepointSchema, subjectId?: string) => {
    const subject = data?.subjects?.find((x) => x.subject_id === subjectId)
    return (
      subject?.study_timepoints?.map((timepoint) => ({
        name: timepoint.name,
        id: timepoint.timepoint_id,
        maximumAllowedTimepoints: timepoint.max_number_of_timepoint_submissions,
        children:
          timepoint.timepoint_submissions?.map((submission) => ({
            name: submission.name,
            id: submission?.timepoint_submission_id,
          })) || [],
      })) || []
    )
  }

  const timePoints = subjectTimepointsData ? transformTimePoints(subjectTimepointsData, subjectId) : []
  const hasAnySubmissions = timePoints.some((tp) => tp.children && tp.children.length > 0)

  let mainContent

  if (timepointSubmissionId && isTimePointSubmissionLoading) {
    mainContent = <div>Loading timepoint submission details...</div>
  } else if (!timepointSubmissionId && !hasAnySubmissions) {
    mainContent = (
      <Alert
        severity="info"
        variant="outlined"
        sx={() => ({
          mt: 2,
        })}
      >
        There are no timepoint submissions available for this subject. You can create new submissions using the
        timepoints panel.
      </Alert>
    )
  } else if (!timepointSubmissionId) {
    mainContent = <div>Loading available timepoint submissions...</div>
  } else {
    const selectedSubmission = findSubmissionById(
      subjectTimepointsData?.subjects?.find((x) => x.subject_id === subjectId)?.study_timepoints || [],
      timepointSubmissionId!,
    )

    if (!selectedSubmission) {
      mainContent = (
        <Alert
          severity="error"
          sx={{
            mt: 2,
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.error.light,
            color: theme.palette.mode === 'dark' ? theme.palette.error.light : theme.palette.error.dark,
            border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.error.main : theme.palette.error.light}`,
          }}
        >
          The requested timepoint submission could not be found.
        </Alert>
      )
    } else {
      const containerRequirements = timePointSubmissionData?.data?.timepoint_submission.container_requirements || []

      const mergedFileTypes = Array.from(new Set(containerRequirements.flatMap((item) => item.file_types) || []))
      const mergedModalities = Array.from(
        new Set(containerRequirements.flatMap((item) => (item.modality ? [item.modality] : []))),
      )

      const containerParam =
        containerRequirements.map((item) => {
          const containerSubmission = timePointSubmissionData?.data?.timepoint_submission.container_submissions.find(
            (req) => req.container_requirement_id === item.container_requirement_id,
          )

          const transformedExamMetadata = containerSubmission?.exam_metadata?.map((exam) => ({
            ...exam,
            series: exam.series.map((series) => ({
              ...series,
              qc_series_tags: series.qc_series_tags.map((tag) => ({
                ...tag,
                apply_to_all_series: tag.apply_to_all_series ?? false,
                allow_apply_to_all_series: tag.allow_apply_to_all_series ?? false,
                dicom_tag_id: tag.dicom_tag_id || '',
                original_value: tag.original_value || '',
                new_value: tag.new_value || '',
              })),
            })),
          }))

          return {
            name: item.name,
            type: item.container_type,
            isOptional: item.is_optional || false,
            modality: item.modality as string,
            convertToDicom: item.convert_to_dicom,
            containerSubmissionId: containerSubmission?.container_submission_id || '',
            eFormFields: item.timepoint_container_eform_fields,
            eFormValues: containerSubmission?.container_eform_submission,
            status: containerSubmission?.status,
            noFilesSubmitted: !!containerSubmission?.no_file_submission_reason,
            criteriaCheck: containerSubmission?.criteria_check,
            examMetadata: transformedExamMetadata,
            comments: containerSubmission?.comments,
            noFileSubmissionReason: containerSubmission?.no_file_submission_reason,
            fileTypesAllowed: mergedFileTypes,
            containerFileTypes: item.file_types,
            modalityAllowed: mergedModalities,
            additionalComments: containerSubmission?.additional_comments,
          }
        }) || []

      mainContent = (
        <Stack spacing={SPACING.sectionsSpacing}>
          <SubjectTimePointConfiguration
            timePointFormId={
              timePointSubmissionData?.data?.timepoint_submission.timepoint_requirement?.timepoint_form_id
            }
            eFormFields={
              timePointSubmissionData?.data?.timepoint_submission.timepoint_requirement?.timepoint_form_fields
            }
            subjectData={subjectData}
            eFormValues={timePointSubmissionData?.data?.timepoint_submission.timepoint_form_submission}
            containerParam={containerParam}
            timePoints={timePoints}
            status={selectedSubmission?.status}
            name={selectedSubmission?.name || ''}
          />
        </Stack>
      )
    }
  }

  return (
    <FormProvider {...methods}>
      <Grid container columnSpacing={SPACING.spacingLg}>
        <Grid size={SIZING.gridSizeLg}>{mainContent}</Grid>
        <Grid size={4}>
          <TimePointManager
            timePoints={timePoints}
            selectedTimePointSubmission={timepointSubmissionId || ''}
            viewableSubmissions={viewableSubmissions}
            modalityOptions={modalityOptions}
            onOpenAllInViewer={handleOpenAllInViewer}
          />
        </Grid>
      </Grid>
    </FormProvider>
  )
}
